// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Navigate } from "react-router";
// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------
import { EarningsCard } from "components/Common/Cards/EarningsCard";

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import StyledEarningsPage from "./styles";
import { useStripeFetchTransfersMutation, useGetDomainGymQuery, useStripeGetGymSubscriptionQuery } from "store/api/api";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../../globalAtoms";
import { LoadingMask } from "components/Common/LoadingMask";
import { EarningsTable } from "components/Payments/EarningsTable";

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
const EarningsPage = () => {
	const { gym } = useParams();
	const navigate = useNavigate();
	const user = useRecoilValue(userAtom);
	const domainGymResponse = useGetDomainGymQuery(gym);
	const isUsedSBPayments = useMemo(() => !!(domainGymResponse?.data?.is_used_sb_payments ?? 1), [domainGymResponse]);
	const gymId = domainGymResponse?.data?.id;

	const [transfers, setTransfers] = useState([]);
	const [pendingBalance, setPendingBalance] = useState(0);
	const [totalEarnings, setTotalEarnings] = useState(0);

	const { data: gymSubscription, isLoading: gymSubscriptionIsLoading } = useStripeGetGymSubscriptionQuery(
		{ gymId },
		{ skip: !gymId }
	);
	const [connectedAccountId, setConnectedAccountId] = useState(null);
	const subscribed = !!gymSubscription?.subscription;
	const [fetchTransfersTrigger] = useStripeFetchTransfersMutation();

	useEffect(() => {
		if (user?.gym?.stripe_info?.stripe_connected_account_id) {
			setConnectedAccountId(user.gym.stripe_info.stripe_connected_account_id);
		}
	}, [user]);

	useEffect(() => {
		if (!isUsedSBPayments) {
			navigate("../");
		}
	}, [isUsedSBPayments, navigate]);

	const fetchDetailedEarnings = async () => {
		try {
			const transfers = await fetchTransfersTrigger({
				account_id: connectedAccountId,
				details: true,
			}).unwrap();

			setTransfers(transfers?.details || []);
			setPendingBalance(transfers?.pending_balance);
			setTotalEarnings(transfers?.payouts_sum);
		} catch (error) {
			console.debug("ERROR FETCHING SUBSCRIBER DATA: ", error);
		}
	};

	useEffect(() => {
		if (connectedAccountId) {
			fetchDetailedEarnings();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectedAccountId]);

	return (
		<StyledEarningsPage className="sb-earnings">
			{domainGymResponse?.isLoading || gymSubscriptionIsLoading ? (
				<LoadingMask />
			) : subscribed ? (
				<>
					<div className="sb-earnings__top-section">
						<EarningsCard pendingBalance={pendingBalance} totalEarnings={totalEarnings} />
					</div>

					<div style={{ padding: "8px", paddingTop: "24px" }}>
						<EarningsTable transfers={transfers} />
					</div>
				</>
			) : (
				<Navigate to={`/${gym}/dashboard/select-plan`} />
			)}
		</StyledEarningsPage>
	);
};

export default EarningsPage;
